import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRightLeft, Receipt, XCircle, Printer, Merge, Split, SlidersHorizontal, Users, X, History, MoveRight, CreditCard, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Field } from "@/components/common";
import { ProductPicker } from "@/components/ProductPicker";
import { PaymentDialog, ReceiptDialog } from "@/components/PaymentDialog";
import { SellBlocked } from "@/components/TrialBanner";
import { OptionsDialog } from "@/components/OptionsDialog";
import { OrderCart } from "@/components/comanda/OrderCart";
import { OrderPrint } from "@/components/comanda/OrderPrint";
import { directPrint } from "@/lib/qz";
import { saleReceipt, orderSlip } from "@/lib/receipts";
import { useWallet } from "@/lib/loyalty";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, fmtDateTime, fmtTime, canDo, PAY_LABELS } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const STATUS = { open: "Aberta", closed: "Fechada", cancelled: "Cancelada", merged: "Juntada" };

function PrintView({ c, tenant }) {
  return (
    <div className="hidden print:block print-area p-2 font-mono text-xs">
      <p className="text-center font-bold">{tenant.nome_fantasia}</p>
      <p className="text-center">CONFERÊNCIA · Comanda #{c.number}{c.table_number ? ` · Mesa ${c.table_number}` : ""}</p>
      <p className="text-center">{fmtDateTime(new Date().toISOString())}</p>
      <p className="border-t border-dashed my-2" />
      {c.items.filter((i) => i.status !== "cancelled").map((i) => <div key={i.id} className="flex justify-between"><span>{i.qty}x {i.name}</span><span>{brl(i.total)}</span></div>)}
      <p className="border-t border-dashed my-2" />
      <div className="flex justify-between"><span>Subtotal</span><span>{brl(c.subtotal)}</span></div>
      {c.discount > 0 && <div className="flex justify-between"><span>Desconto</span><span>-{brl(c.discount)}</span></div>}
      {c.service_fee > 0 && <div className="flex justify-between"><span>Serviço</span><span>{brl(c.service_fee)}</span></div>}
      {c.surcharge > 0 && <div className="flex justify-between"><span>Acréscimo</span><span>{brl(c.surcharge)}</span></div>}
      <div className="flex justify-between font-bold"><span>TOTAL</span><span>{brl(c.total)}</span></div>
      {c.paid > 0 && <div className="flex justify-between"><span>Pago</span><span>{brl(c.paid)}</span></div>}
      <div className="flex justify-between font-bold"><span>RESTANTE</span><span>{brl(c.remaining)}</span></div>
    </div>
  );
}

function ItemRow({ i, open, selected, toggle, run, id, canCancel }) {
  const off = i.status === "cancelled";
  return (
    <li className={cn("flex items-start gap-2 p-3 text-sm", off && "opacity-50")} data-testid={`comanda-item-${i.id}`}>
      {open && !off && !i.paid && <Checkbox checked={selected} onCheckedChange={toggle} className="mt-1" data-testid={`select-item-${i.id}`} />}
      <div className="flex-1 min-w-0">
        <p className={cn("font-medium", off && "line-through")}>{i.name} {i.paid && <span className="ml-1 rounded bg-emerald-500/15 px-1.5 text-[10px] text-emerald-500">PAGO</span>}</p>
        <p className="text-xs text-muted-foreground">{i.added_by} · {fmtTime(i.added_at)}{off ? ` · cancelado${i.cancel_reason ? `: ${i.cancel_reason}` : ""}` : ""}</p>
        {i.notes && <p className="text-xs text-amber-600 dark:text-amber-400">{i.notes}</p>}
      </div>
      <span className="font-mono text-xs text-muted-foreground">{i.qty}x</span>
      <span className="w-20 text-right font-mono">{brl(i.total)}</span>
      {open && !off && !i.paid && canCancel && (
        <button onClick={() => { const r = window.prompt(`Motivo do cancelamento de ${i.name}:`); if (r !== null) run(() => api.post(`/comandas/${id}/items/${i.id}/cancel`, { reason: r })); }} data-testid={`cancel-item-${i.id}`}>
          <X className="h-4 w-4 text-muted-foreground hover:text-red-500" />
        </button>
      )}
    </li>
  );
}

function groupOrders(c) {
  const groups = [];
  c.items.forEach((i) => {
    const k = i.order_id || "old";
    let g = groups.find((x) => x.k === k);
    if (!g) groups.push((g = { k, order: (c.orders || []).find((o) => o.id === i.order_id), items: [] }));
    g.items.push(i);
  });
  return groups;
}

function OrderHeader({ g, open, canCancel, onCancel }) {
  const o = g.order;
  const cancellable = g.items.some((i) => i.status !== "cancelled" && !i.paid);
  return (
    <li className="flex items-center justify-between gap-2 bg-muted/50 px-3 py-2 text-xs" data-testid={`order-group-${o?.number || "old"}`}>
      <span className="font-semibold">{o ? `Pedido ${o.number} · ${fmtTime(o.at)} · ${o.user}` : "Itens lançados anteriormente"}{o?.pickup_number && <span className="ml-2 rounded bg-primary/15 px-1.5 text-primary">Senha {String(o.pickup_number).padStart(3, "0")}</span>}</span>
      {o && open && canCancel && cancellable && <button className="text-red-500 hover:underline" onClick={onCancel} data-testid={`cancel-order-${o.number}`}>Cancelar pedido</button>}
    </li>
  );
}

function Totals({ c }) {
  const row = (l, v, cls = "") => <div className={cn("flex justify-between", cls)}><span>{l}</span><span className="font-mono">{v}</span></div>;
  return (
    <div className="space-y-1 text-sm" data-testid="comanda-totals">
      {row("Subtotal", brl(c.subtotal))}
      {c.discount > 0 && row("Descontos", `- ${brl(c.discount)}`, "text-red-500")}
      {c.service_fee > 0 && row("Taxa de serviço", brl(c.service_fee))}
      {c.surcharge > 0 && row("Acréscimos", brl(c.surcharge))}
      <div className="flex items-end justify-between border-t pt-2"><span className="text-muted-foreground">Total</span><span className="font-display text-3xl font-bold" data-testid="comanda-total">{brl(c.total)}</span></div>
      {c.paid > 0 && row("Pago (parcial)", brl(c.paid), "text-emerald-500")}
      {c.paid > 0 && row("Restante", brl(c.remaining), "font-bold")}
    </div>
  );
}

export default function ComandaView() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [sel, setSel] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [options, setOptions] = useState([]);
  const [picking, setPicking] = useState(null);
  const [pay, setPay] = useState(null);
  const [sale, setSale] = useState(null);
  const [adj, setAdj] = useState({ discount: 0, surcharge: 0, apply_service_fee: false });
  const [cart, setCart] = useState([]);
  const [sending, setSending] = useState(false);
  const [printOrder, setPrintOrder] = useState(null);
  const wallet = useWallet(c?.customer_id);
  const navigate = useNavigate();
  const { session } = useAuth();
  const load = useCallback(() => api.get(`/comandas/${id}`).then((r) => { setC(r.data); setSel([]); }), [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    try {
      const r = await fn();
      if (r?.data?.items && r.data.id === id) { setC(r.data); setSel([]); } else await load();
      if (msg) toast.success(msg);
      return r;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const addToCart = ({ product: p, options = [], qty = 1, notes = "", name, price }) => setCart((cs) => {
    const key = `${p.id}|${JSON.stringify(options)}|${notes}`;
    return cs.some((i) => i.key === key) ? cs.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i))
      : [...cs, { key, product_id: p.id, name: name || p.name, price: price ?? p.price, qty, options, notes }];
  });
  const sendOrder = async () => {
    setSending(true);
    const r = await run(() => api.post(`/comandas/${id}/items`, { items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty, notes: i.notes, options: i.options })) }), "Pedido enviado");
    setSending(false);
    if (!r?.data?.last_order) return;
    setCart([]);
    const o = r.data.last_order;
    const store = session.tenant.nome_fantasia;
    const pending = [];
    if (o.items.length && !(await directPrint(session, "comanda", (pr) => orderSlip(o, "COMANDA", o.items, store, pr)))) pending.push("main");
    for (const s of o.sectors) if (!(await directPrint(session, `sector_${s.id}`, (pr) => orderSlip(o, s.sector.toUpperCase(), s.items, store, pr)))) pending.push(s.id);
    if (!pending.length) return;
    setPrintOrder({ ...o, pending });
    setTimeout(() => { window.print(); setPrintOrder(null); }, 300);
  };
  const cancelOrder = (o) => { const r = window.prompt(`Motivo do cancelamento do pedido ${o.number}:`); if (r !== null) run(() => api.post(`/comandas/${id}/orders/${o.id}/cancel`, { reason: r }), "Pedido cancelado"); };
  const openDialog = async (kind) => {
    if (kind === "transfer") setOptions((await api.get("/tables")).data.filter((t) => !t.comanda_id));
    if (kind === "merge" || kind === "move") setOptions((await api.get("/comandas?status=open")).data.filter((x) => x.id !== id));
    if (kind === "adjust") setAdj({ discount: c.discount, surcharge: c.surcharge, apply_service_fee: !!c.apply_service_fee });
    setDialog(kind);
  };
  const choose = (targetId) => {
    const map = {
      transfer: () => api.post(`/comandas/${id}/transfer`, { table_id: targetId }),
      merge: () => api.post(`/comandas/${id}/merge`, { source_ids: [targetId] }),
      move: () => api.post(`/comandas/${id}/move-items`, { item_ids: sel, target_comanda_id: targetId }),
    };
    setDialog(null);
    run(map[dialog], "Operação concluída");
  };
  const split = async () => {
    const name = window.prompt("Identificação da nova comanda:");
    if (name === null) return;
    const r = await run(() => api.post(`/comandas/${id}/split`, { item_ids: sel, customer_name: name }), "Comanda dividida");
    if (r?.data?.id) navigate(`/app/comandas/${r.data.id}`);
  };
  const partialBy = (mode) => {
    if (mode === "items") return setPay({ amount: c.items.filter((i) => sel.includes(i.id)).reduce((s, i) => s + i.total, 0), item_ids: sel });
    const n = Number(window.prompt("Dividir o restante por quantas pessoas?", "2"));
    if (n >= 2) setPay({ amount: Math.round((c.remaining / n) * 100) / 100, item_ids: [], label: `1/${n} da conta` });
  };
  const confirmPay = async ({ payments, loyalty_rewards, loyalty_discount }) => {
    if (pay.close) {
      const s = (await api.post(`/comandas/${id}/close`, { payments, loyalty_rewards, loyalty_discount })).data;
      setSale(s);
      directPrint(session, "pdv", (pr) => saleReceipt(s, session.tenant, pr));
      return;
    }
    let change = Math.round((payments.reduce((s, p) => s + p.amount, 0) - pay.amount) * 100) / 100;
    const adjusted = payments.map((p) => { if (change > 0 && p.method === "dinheiro") { const cut = Math.min(change, p.amount); change -= cut; return { ...p, amount: p.amount - cut }; } return p; }).filter((p) => p.amount > 0);
    const { data } = await api.post(`/comandas/${id}/partial-payment`, { payments: adjusted, item_ids: pay.item_ids });
    setC(data);
    setSel([]);
    toast.success("Pagamento parcial registrado");
  };

  if (!c) return null;
  const open = c.status === "open";
  const canCancel = canDo(session.user, "cancel_sale");
  return (
    <>
      <Link to={c.table_id ? "/app/mesas" : "/app/comandas"} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="comanda-back-link"><ArrowLeft className="h-4 w-4" />Voltar</Link>
      <PageHeader eyebrow={`${c.table_number ? `Mesa ${c.table_number}` : "Comanda avulsa"} · ${STATUS[c.status]}`} title={`Comanda #${c.number}`}
        subtitle={`${c.customer_name || "Sem identificação"}${c.people ? ` · ${c.people} pessoa(s)` : ""} · aberta às ${fmtTime(c.opened_at)} por ${c.opened_by}`}
        actions={<>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-partial-btn"><Printer className="h-4 w-4 mr-1" />{open ? "Imprimir parcial" : "Imprimir"}</Button>
          <Button variant="outline" onClick={() => setDialog("history")} data-testid="comanda-history-btn"><History className="h-4 w-4 mr-1" />Histórico</Button>
          {open && <>
            <Button variant="outline" onClick={() => openDialog("transfer")} data-testid="transfer-comanda-btn"><ArrowRightLeft className="h-4 w-4 mr-1" />Transferir mesa</Button>
            <Button variant="outline" onClick={() => openDialog("merge")} data-testid="merge-comanda-btn"><Merge className="h-4 w-4 mr-1" />Juntar comanda</Button>
            {canDo(session.user, "cancel_sale") && <Button variant="outline" onClick={() => { const r = window.prompt("Motivo do cancelamento da comanda:"); if (r !== null) run(() => api.post(`/comandas/${id}/cancel`, { reason: r })).then(() => navigate("/app/comandas")); }} data-testid="cancel-comanda-btn"><XCircle className="h-4 w-4 mr-1" />Cancelar</Button>}
          </>}
          {c.status === "closed" && canDo(session.user, "reopen_comanda") && <Button variant="outline" onClick={() => { const r = window.prompt("Motivo da reabertura da comanda:"); if (r !== null) run(() => api.post(`/comandas/${id}/reopen`, { reason: r }), "Comanda reaberta"); }} data-testid="reopen-comanda-btn"><RotateCcw className="h-4 w-4 mr-1" />Reabrir comanda</Button>}
        </>} />
      <SellBlocked />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {open && <div className="lg:col-span-6"><ProductPicker compact onPick={(p, qty) => (p.option_groups?.length ? setPicking(p) : addToCart({ product: p, qty }))} /></div>}
        <div className={cn("space-y-4", open ? "lg:col-span-6" : "lg:col-span-12")}>
          {open && <OrderCart cart={cart} setCart={setCart} onSend={sendOrder} sending={sending} />}
          <div className="card-surface" data-testid="comanda-items">
            <div className="flex items-center justify-between border-b p-4">
              <p className="font-display font-semibold">Itens consumidos</p>
              {open && sel.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid="selection-toolbar">
                  <Button size="sm" variant="outline" onClick={() => openDialog("move")} data-testid="move-items-btn"><MoveRight className="h-4 w-4 mr-1" />Transferir</Button>
                  <Button size="sm" variant="outline" onClick={split} data-testid="split-comanda-btn"><Split className="h-4 w-4 mr-1" />Dividir</Button>
                  <Button size="sm" onClick={() => partialBy("items")} data-testid="pay-selected-btn">Pagar ({sel.length})</Button>
                </div>
              )}
            </div>
            <ul className="divide-y max-h-[45vh] overflow-y-auto">
              {groupOrders(c).map((g) => [
                <OrderHeader key={`h-${g.k}`} g={g} open={open} canCancel={canCancel} onCancel={() => cancelOrder(g.order)} />,
                ...g.items.map((i) => <ItemRow key={`${i.id}-${i.qty}`} i={i} id={id} open={open} run={run} canCancel={canCancel} selected={sel.includes(i.id)} toggle={() => setSel((s) => (s.includes(i.id) ? s.filter((x) => x !== i.id) : [...s, i.id]))} />),
              ])}
              {!c.items.length && <li className="p-8 text-center text-sm text-muted-foreground">Nenhum pedido feito.</li>}
            </ul>
            <div className="border-t p-4 space-y-3">
              <Totals c={c} />
              {open ? (<>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => openDialog("adjust")} data-testid="adjust-comanda-btn"><SlidersHorizontal className="h-4 w-4 mr-1" />Desconto / taxas</Button>
                  <Button variant="outline" disabled={!c.remaining} onClick={() => partialBy("people")} data-testid="split-people-btn"><Users className="h-4 w-4 mr-1" />Dividir por pessoas</Button>
                  <Button variant="outline" className="col-span-2" disabled={!c.remaining} onClick={() => { const v = Number(window.prompt("Valor do pagamento parcial (R$):")); if (v > 0) setPay({ amount: Math.min(v, c.remaining), item_ids: [] }); }} data-testid="partial-payment-btn"><CreditCard className="h-4 w-4 mr-1" />Pagamento parcial</Button>
                </div>
                <Button className="w-full h-14 text-lg" disabled={!c.items.some((i) => i.status !== "cancelled")} onClick={() => (cart.length && !window.confirm("Há itens no novo pedido que ainda não foram pedidos. Fechar a comanda mesmo assim?") ? null : setPay({ amount: c.remaining, close: true }))} data-testid="close-table-bill-btn"><Receipt className="h-5 w-5 mr-2" />Fechar comanda · {brl(c.remaining)}</Button>
              </>) : <p className="text-center text-sm text-muted-foreground">Comanda {STATUS[c.status].toLowerCase()} {c.closed_at ? `em ${fmtDateTime(c.closed_at)}` : ""}.</p>}
              {c.payments?.length > 0 && <ul className="text-xs text-muted-foreground space-y-0.5" data-testid="partial-payments-list">{c.payments.map((p, k) => <li key={k}>{fmtTime(p.at)} · {PAY_LABELS[p.method]} {brl(p.amount)} · {p.user}</li>)}</ul>}
            </div>
          </div>
        </div>
      </div>
      {printOrder ? <OrderPrint order={printOrder} store={session.tenant.nome_fantasia} /> : <PrintView c={c} tenant={session.tenant} />}
      <OptionsDialog product={picking} onClose={() => setPicking(null)} onAdd={addToCart} />
      <PaymentDialog open={!!pay} onOpenChange={(o) => !o && setPay(null)} subtotal={pay?.amount || 0} allowDiscount={false} loyalty={pay?.close && wallet?.enabled ? wallet : null}
        title={pay?.close ? `Fechar comanda #${c.number}` : `Pagamento parcial${pay?.label ? ` · ${pay.label}` : ""}`} onConfirm={confirmPay} />
      <ReceiptDialog sale={sale} onClose={() => navigate(c.table_id ? "/app/mesas" : "/app/comandas")} />
      <Dialog open={["transfer", "merge", "move"].includes(dialog)} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="choose-target-modal">
          <DialogHeader><DialogTitle>{dialog === "transfer" ? "Transferir para a mesa" : dialog === "merge" ? "Juntar com a comanda" : "Transferir itens para a comanda"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
            {options.map((o) => (
              <Button key={o.id} variant="outline" className="h-16 flex-col" onClick={() => choose(o.id)} data-testid={`target-${o.number}`}>
                <span className="text-lg font-bold">{dialog === "transfer" ? `Mesa ${o.number}` : `#${o.number}`}</span>
                <span className="text-[10px] text-muted-foreground truncate max-w-full">{dialog === "transfer" ? o.name : o.customer_name || (o.table_number ? `Mesa ${o.table_number}` : "Avulsa")}</span>
              </Button>
            ))}
            {!options.length && <p className="col-span-3 text-sm text-muted-foreground">Nenhuma opção disponível.</p>}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={dialog === "adjust"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="adjust-modal">
          <DialogHeader><DialogTitle>Descontos e taxas</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Desconto (R$)"><Input type="number" step="0.01" value={adj.discount} onChange={(e) => setAdj((a) => ({ ...a, discount: e.target.value }))} data-testid="adjust-discount-input" /></Field>
            <Field label="Acréscimo (R$)"><Input type="number" step="0.01" value={adj.surcharge} onChange={(e) => setAdj((a) => ({ ...a, surcharge: e.target.value }))} data-testid="adjust-surcharge-input" /></Field>
            <label className="col-span-2 flex items-center justify-between rounded-lg border px-3 py-2 text-sm">Taxa de serviço ({session.tenant.settings.service_fee_pct}%)
              <Switch checked={adj.apply_service_fee} onCheckedChange={(v) => setAdj((a) => ({ ...a, apply_service_fee: v }))} data-testid="adjust-service-fee-switch" /></label>
          </div>
          <DialogFooter><Button onClick={() => { setDialog(null); run(() => api.post(`/comandas/${id}/adjust`, { discount: Number(adj.discount) || 0, surcharge: Number(adj.surcharge) || 0, apply_service_fee: adj.apply_service_fee }), "Ajustes aplicados"); }} data-testid="save-adjust-btn">Aplicar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={dialog === "history"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto" data-testid="history-modal">
          <DialogHeader><DialogTitle>Histórico de operações</DialogTitle></DialogHeader>
          <ul className="space-y-2 text-sm">{[...(c.history || [])].reverse().map((h, k) => <li key={k} className="border-l-2 border-primary/40 pl-3"><p>{h.action}</p><p className="text-xs text-muted-foreground">{fmtDateTime(h.at)} · {h.user}</p></li>)}</ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
